import { CvValue, EditorialProject } from '@/models'
import ApiClient from '@/services/api-client'
import { CV_VALUE_MATERIAL_TYPE, CV_VALUE_SHORT_CODE_INDEF } from '@/utils/constants'

/**
 * Product form fields shown depending on the material types of the production project.
 *
 * - Fixed fields are always shown (the organizational levels depend on the project instead).
 * - A field listed in the metadata of some material type (comma-separated field names) is only
 *   shown when the production project has one of those material types. It is then required,
 *   unless its name ends with '?' in every one of them (e.g. 'collection,series?').
 * - Any other field is always shown and optional.
 */
export const PRODUCT_FIXED_REQUIRED_FIELDS = [
  'code', 'name', 'type', 'editorialProject', 'country', 'campaign', 'productionCampaign'
]
export const PRODUCT_FIXED_OPTIONAL_FIELDS = ['language', 'region', 'wwIssueId', 'wwIssue', 'wwBrandId', 'wwBrand']
// Switches always have a value, so they never block the form
export const PRODUCT_BOOLEAN_FIELDS = ['assetsActive', 'sendBtovenFromStructureLdx']

const OPTIONAL_FIELD_SUFFIX = '?'

const splitList = (value: string | null | undefined): string[] =>
  (value || '').split(',').map(item => item.trim()).filter(item => item)

const fieldName = (item: string): string =>
  item.endsWith(OPTIONAL_FIELD_SUFFIX) ? item.slice(0, -OPTIONAL_FIELD_SUFFIX.length).trim() : item

const typeFields = (types: CvValue[]): string[] =>
  types.reduce((all: string[], type) => all.concat(splitList(type.metadata)), [])

export class ProductFieldRules {
  private readonly assignedFields: Set<string>
  private readonly projectFields: Set<string>
  private readonly projectRequiredFields: Set<string>

  constructor(materialTypes: CvValue[], project?: EditorialProject | null) {
    const projectTypes = splitList(project ? project.materialTypes : null)
    const projectItems = typeFields(materialTypes.filter(type => projectTypes.includes(type.codeId)))
    this.assignedFields = new Set(typeFields(materialTypes).map(fieldName))
    this.projectFields = new Set(projectItems.map(fieldName))
    this.projectRequiredFields = new Set(projectItems.filter(item => !item.endsWith(OPTIONAL_FIELD_SUFFIX)))
  }

  private isFixed(field: string): boolean {
    return PRODUCT_FIXED_REQUIRED_FIELDS.includes(field) || PRODUCT_FIXED_OPTIONAL_FIELDS.includes(field)
  }

  isVisible(field: string): boolean {
    return this.isFixed(field) || !this.assignedFields.has(field) || this.projectFields.has(field)
  }

  isRequired(field: string): boolean {
    if (PRODUCT_FIXED_REQUIRED_FIELDS.includes(field)) return true
    if (PRODUCT_BOOLEAN_FIELDS.includes(field) || this.isFixed(field)) return false
    return this.projectRequiredFields.has(field)
  }
}

/**
 * Material types with their fields, read skipping the HTTP cache so a change made in the
 * controlled vocabulary section applies straight away
 */
export async function loadMaterialTypes(): Promise<CvValue[]> {
  const values = await ApiClient.getCvValuesByType(CV_VALUE_MATERIAL_TYPE, true)
  return values.filter(item => item.shortCode !== CV_VALUE_SHORT_CODE_INDEF)
}
